// (helper components appended below OpsRoomPage)

/*
 OK FBI — Standalone Operations Room route (/ops)
 Works BEFORE any case is opened. Ideal for the pre-game live test:
   host connects TikTok LIVE → asks viewers to type anything → verifies
   real comments arrive → without needing to open a case first.
 When a case is active, the room automatically binds to it and can start
 SUSPECT_POLL / FINAL_SUSPECT voting sessions from here.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { useSession } from "@/contexts/SessionContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Users, Vote, StopCircle, Play, Tv } from "lucide-react";
import { TikTokConnectStrip, InvestigatorMeetingRoom } from "@/components/InvestigatorMeetingRoom";
import { StreamPlatformsManager } from "@/components/streamer/StreamPlatformsManager";

// Simple case fetcher — mirrors the pattern in CaseDetail.
async function getCase(caseId) {
  try {
    const { data } = await api.get(`/cases/${caseId}`);
    return data;
  } catch {
    return null;
  }
}

export default function OpsRoomPage() {
  const { session } = useSession();
  const caseId = session?.current_case_id || null;
  const [c, setC] = useState(null);
  const [vmode, setVmode] = useState({ mode: "NONE", status: "CLOSED", tally: {}, unique_voters: 0 });
  const [obsCfg, setObsCfg] = useState({});

  useEffect(() => {
    if (!caseId) { setC(null); return; }
    getCase(caseId).then(setC).catch(() => setC(null));
  }, [caseId]);

  const loadMode = useCallback(async () => {
    try { const { data } = await api.get("/voting/mode"); setVmode(data || {}); } catch {}
  }, []);
  useEffect(() => {
    loadMode();
    api.get("/overlay/state").then(({ data }) => setObsCfg(data?.config || {})).catch(() => {});
    const iv = setInterval(loadMode, 2000);
    return () => clearInterval(iv);
  }, [loadMode]);

  const startSuspectPoll = async (final = false) => {
    if (!c) { toast.error("افتح قضية أولاً"); return; }
    const suspects = (c.suspects || []).map(s => ({ id: s.id, name: s.name }));
    try {
      await api.post("/voting/mode", {
        mode: final ? "FINAL_SUSPECT" : "SUSPECT_POLL",
        case_id: caseId, suspects,
        title: final ? "من المتهم؟" : "من الأكثر إثارة للشك؟",
        open_now: true,
      });
      await api.post("/session", { voting_status: "OPEN" });
      loadMode();
      toast.success(final ? "بدأ التصويت النهائي" : "بدأ تصويت المشتبه بهم");
    } catch { toast.error("تعذر بدء التصويت"); }
  };

  const closeVote = async () => {
    try {
      await api.post("/session", { voting_status: "CLOSED" });
      loadMode();
      toast.success("تم إغلاق التصويت");
    } catch {}
  };
  const resetVoting = async () => {
    try {
      await api.post("/voting/mode", { mode: "NONE" });
      loadMode();
    } catch {}
  };

  const toggleObsMirror = async () => {
    const next = !obsCfg.show_meeting_room;
    try { await api.patch("/session/overlay-config", { show_meeting_room: next }); setObsCfg(x => ({ ...x, show_meeting_room: next })); } catch {}
  };

  return (
    <Shell>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Radio size={18} className="text-gold" />
            <h1 className="text-xl tracking-[0.25em] text-gold font-cairo">غرفة العمليات</h1>
          </div>
          <div className="text-[10px] font-mono-ar text-zinc-500">
            القضية الحالية: <span className="text-gold">{caseId || "— لا توجد قضية نشطة —"}</span>
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 font-cairo mb-4">مركز متابعة البث والتصويت المباشر</div>

        {/* Multi-Platform Broadcast Manager (Twitch · Kick · TikTok) */}
        <div className="mb-6">
          <StreamPlatformsManager />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-3">
            <TikTokConnectStrip caseId={caseId} suspects={c?.suspects || []} />
            <InvestigatorMeetingRoom caseId={caseId} suspects={c?.suspects || []} mini={false} showControls={true} />
          </div>

          <div className="space-y-3">
            {/* Central voting controls */}
            <div className="panel p-4 border-metal" data-testid="ops-voting-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Vote size={14} className="text-gold" />
                  <div className="text-[12px] tracking-[0.25em] text-gold font-cairo">التصويت المركزي</div>
                </div>
                <div className={`text-[10px] font-mono-ar px-2 py-0.5 border ${vmode.status === "OPEN" ? "text-red-300 border-red-800 bg-red-950/20" : "text-zinc-500 border-metal"}`}>
                  {vmode.status === "OPEN" ? "🔴 مباشر" : "مغلق"}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 font-cairo mb-3">
                الوضع: <span className="text-gold font-mono-ar">{vmode.mode}</span>
              </div>

              {!c && (
                <div className="text-[11px] text-amber-300 font-cairo border border-amber-800 bg-amber-950/20 p-2 mb-3">
                  افتح قضية أولاً لتفعيل التصويت على المشتبه بهم
                </div>
              )}

              {vmode.mode === "NONE" && c && (
                <div className="space-y-1.5">
                  <button onClick={() => startSuspectPoll(false)} data-testid="ops-start-suspect-poll"
                    className="btn-primary text-[11px] w-full py-1.5 flex items-center justify-center gap-1">
                    <Users size={11} /> تصويت المشتبه بهم
                  </button>
                  <button onClick={() => startSuspectPoll(true)} data-testid="ops-start-final-vote"
                    className="w-full py-1.5 text-[11px] border border-red-700 text-red-300 hover:bg-red-950/30 flex items-center justify-center gap-1">
                    <Play size={11} /> التصويت النهائي على المتهم
                  </button>
                  <ForensicVoteLauncher c={c} caseId={caseId} onStarted={loadMode} />
                </div>
              )}

              {vmode.mode !== "NONE" && (
                <div className="space-y-2">
                  {/* Tally list */}
                  <VotingTally vmode={vmode} />
                  <div className="grid grid-cols-2 gap-1 pt-2 border-t border-metal/40">
                    {vmode.status === "OPEN" ? (
                      <button onClick={closeVote} data-testid="ops-close-vote" className="text-[11px] py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center gap-1">
                        <StopCircle size={11} /> إغلاق التصويت
                      </button>
                    ) : (
                      <button onClick={async () => { try { await api.post("/session", { voting_status: "OPEN" }); loadMode(); } catch {} }} className="text-[11px] py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.12)]">
                        فتح التصويت
                      </button>
                    )}
                    <button onClick={resetVoting} data-testid="ops-reset-vote" className="text-[11px] py-1 border border-metal text-zinc-400 hover:border-red-600 hover:text-red-300">
                      إنهاء الجلسة
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* OBS mirror toggle */}
            <div className="panel p-3 border-metal">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-cairo text-zinc-400">إظهار غرفة العمليات في البث</div>
                <button onClick={toggleObsMirror} data-testid="ops-obs-toggle"
                  className={`text-[11px] px-2 py-1 border ${obsCfg.show_meeting_room ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400 hover:border-gold hover:text-gold"}`}>
                  {obsCfg.show_meeting_room ? "تشغيل" : "إيقاف"}
                </button>
              </div>
            </div>

            {caseId ? (
              <Link to={`/case/${caseId}`} className="block panel p-3 border-metal text-center text-[11px] text-gold hover:bg-[rgba(197,160,89,0.06)] font-cairo">
                رجوع إلى صفحة القضية
              </Link>
            ) : (
              <Link to="/cases" className="block panel p-3 border-metal text-center text-[11px] text-gold hover:bg-[rgba(197,160,89,0.06)] font-cairo">
                فتح قائمة القضايا
              </Link>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function VotingTally({ vmode }) {
  const ctx = vmode.context || {};
  const choices = ctx.methods && ctx.methods.length ? ctx.methods : (ctx.suspects || []);
  const tally = vmode.tally || {};
  const total = vmode.unique_voters || 0;
  const rows = choices.map((ch, i) => {
    const count = tally[ch.id] || 0;
    return { idx: i + 1, id: ch.id, label: ch.label || ch.name, count, pct: total > 0 ? Math.round((count / total) * 100) : 0 };
  }).sort((a, b) => b.count - a.count);
  const winner = rows[0]?.count > 0 ? rows[0] : null;
  return (
    <div className="space-y-1" data-testid="ops-voting-tally">
      {ctx.title && <div className="text-[11px] text-gold font-cairo mb-1">{ctx.title}</div>}
      {rows.map(r => (
        <div key={r.id} className="flex items-center gap-2 text-[11px] font-cairo" data-testid={`ops-tally-row-${r.id}`}>
          <span className="text-gold font-mono-ar w-5 text-center">#{r.idx}</span>
          <span className="text-white flex-1 truncate">{r.label}</span>
          <span className="text-zinc-500 font-mono-ar tabular-nums w-8 text-left">{r.count}</span>
          <div className="h-1.5 w-16 bg-black/40 border border-metal/40 relative overflow-hidden">
            <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${Math.min(100, r.pct)}%` }} />
          </div>
          <span className="text-gold font-mono-ar tabular-nums w-8 text-left">{r.pct}%</span>
        </div>
      ))}
      <div className="pt-2 border-t border-metal/40 text-[10px] text-zinc-500 font-cairo">
        إجمالي المصوتين: <span className="text-white font-mono-ar tabular-nums">{total}</span>
      </div>
      {vmode.status !== "OPEN" && winner && (
        <div className="pt-2 mt-1 border-t border-gold/50 text-[11px] font-cairo bg-[rgba(197,160,89,0.06)] p-2" data-testid="ops-vote-winner">
          <div className="text-zinc-400 mb-0.5">قرار المحققين</div>
          <div className="text-gold">{winner.label} · <span className="font-mono-ar">{winner.count}</span> صوت</div>
        </div>
      )}
    </div>
  );
}

// ===================== ForensicVoteLauncher =====================
// Choose an evidence from the current case, pick which methods to offer,
// then start a FORENSIC_METHOD vote. All methods are inferred from
// the evidence.analysis field of the current case when available;
// falls back to a standard 3-method set otherwise.
function ForensicVoteLauncher({ c, caseId, onStarted }) {
  const [expanded, setExpanded] = useState(false);
  const [evId, setEvId] = useState("");
  const [picked, setPicked] = useState({});
  const evidences = (c?.evidence || []).slice();
  const selectedEv = evidences.find(e => e.id === evId);

  const ALL_METHODS = [
    { id: "FINGERPRINT", label: "البصمات" },
    { id: "DNA", label: "DNA" },
    { id: "VOICE", label: "الصوت" },
    { id: "DOCUMENT", label: "الوثائق" },
    { id: "PHONE", label: "الهاتف" },
    { id: "MATERIALS", label: "المواد" },
  ];

  // Infer allowed methods from evidence content: prefer the tool the case already lists,
  // plus any methods the evidence hints at via its analysis text / type.
  const inferAllowed = (ev) => {
    if (!ev) return [];
    const allow = new Set();
    const t = String(ev.tool || "").toUpperCase();
    if (t.includes("FINGER")) allow.add("FINGERPRINT");
    if (t.includes("DNA")) allow.add("DNA");
    if (t.includes("VOICE") || t.includes("AUDIO")) allow.add("VOICE");
    if (t.includes("DOC")) allow.add("DOCUMENT");
    if (t.includes("PHONE") || t.includes("CALL")) allow.add("PHONE");
    if (t.includes("MATERIAL") || t.includes("TOX") || t.includes("CHEM")) allow.add("MATERIALS");
    // Broaden by evidence-type keywords
    const type = String(ev.type || "").toLowerCase();
    const txt = String((ev.analysis && (ev.analysis.result || ev.analysis.text)) || "").toLowerCase();
    if (type.includes("photo") || type.includes("image") || type.includes("evidence")) allow.add("FINGERPRINT");
    if (txt.includes("dna") || type.includes("bio")) allow.add("DNA");
    if (type.includes("audio") || type.includes("voice")) allow.add("VOICE");
    if (type.includes("phone") || type.includes("sms") || type.includes("call")) allow.add("PHONE");
    if (type.includes("document") || type.includes("paper") || type.includes("note")) allow.add("DOCUMENT");
    if (type.includes("cctv") || type.includes("video")) allow.add("VOICE");   // CCTV audio pass
    if (txt.includes("material") || txt.includes("toxin") || txt.includes("سم") || txt.includes("مادة")) allow.add("MATERIALS");
    // If nothing inferred, give a safe minimum
    if (allow.size === 0) { allow.add("FINGERPRINT"); allow.add("DNA"); allow.add("MATERIALS"); }
    return ALL_METHODS.filter(m => allow.has(m.id));
  };

  const suggested = inferAllowed(selectedEv);

  useEffect(() => {
    if (!selectedEv) { setPicked({}); return; }
    const initial = {};
    suggested.forEach(m => { initial[m.id] = true; });
    setPicked(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evId]);

  const start = async () => {
    if (!selectedEv) { toast.error("اختر دليلاً أولاً"); return; }
    const methods = suggested.filter(m => picked[m.id]);
    if (methods.length < 2) { toast.error("اختر طريقتين على الأقل"); return; }
    try {
      await api.post("/voting/mode", {
        mode: "FORENSIC_METHOD",
        case_id: caseId,
        evidence_id: selectedEv.id,
        methods,
        title: `كيف نفحص هذا الدليل؟ · ${selectedEv.title || selectedEv.id}`,
        open_now: true,
      });
      await api.post("/session", { voting_status: "OPEN" });
      setExpanded(false);
      onStarted && onStarted();
      toast.success("بدأ تصويت طريقة الفحص");
    } catch { toast.error("تعذر بدء تصويت الفحص"); }
  };

  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)} data-testid="ops-start-forensic-vote"
        className="w-full py-1.5 text-[11px] border border-metal text-zinc-200 hover:border-gold hover:text-gold flex items-center justify-center gap-1">
        🎯 اطلب قرار المحققين على فحص دليل
      </button>
    );
  }

  return (
    <div className="border border-metal p-2 space-y-2" data-testid="ops-forensic-launcher">
      <div className="text-[10px] font-cairo text-gold">اختر الدليل</div>
      <select value={evId} onChange={(e) => setEvId(e.target.value)}
        data-testid="ops-forensic-ev-select"
        className="w-full bg-black/40 border border-metal px-2 py-1 text-[11px] text-white outline-none focus:border-gold font-cairo">
        <option value="">— اختر —</option>
        {evidences.map(ev => (
          <option key={ev.id} value={ev.id}>{ev.id} · {ev.title}</option>
        ))}
      </select>
      {selectedEv && (
        <>
          <div className="text-[10px] font-cairo text-gold pt-1">اختر طرق الفحص المتاحة لهذا الدليل</div>
          <div className="grid grid-cols-2 gap-1">
            {suggested.map(m => (
              <label key={m.id} className={`text-[11px] font-cairo px-2 py-1 border flex items-center gap-1 cursor-pointer ${picked[m.id] ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400"}`}>
                <input type="checkbox" className="hidden" checked={!!picked[m.id]}
                  onChange={() => setPicked(p => ({ ...p, [m.id]: !p[m.id] }))} />
                {m.label}
              </label>
            ))}
          </div>
          {suggested.length === 0 && (
            <div className="text-[10px] text-amber-300 font-cairo">لا توجد طرق فحص متوافقة مع هذا الدليل</div>
          )}
        </>
      )}
      <div className="grid grid-cols-2 gap-1 pt-1 border-t border-metal/40">
        <button onClick={start} data-testid="ops-forensic-start"
          className="text-[11px] py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.12)]">بدء التصويت</button>
        <button onClick={() => setExpanded(false)}
          className="text-[11px] py-1 border border-metal text-zinc-400 hover:text-white">إلغاء</button>
      </div>
    </div>
  );
}

